import { useUserStore } from '@/stores/userStore'
import { useTeamStore } from '@/stores/teamStore'
import type { ActionType, Role } from '@/types/role'

export function usePermissions() {
  const userStore = useUserStore()
  const teamStore = useTeamStore()

  /**
   * Return only the roles whose per-role context applies to the currently
   * selected team. Roles without a team context (e.g. tpa_admin, org_admin)
   * are always kept; team-scoped roles require the active team id to be
   * listed under `role.context.teams`.
   */
  const getEffectiveRoles = (): Role[] => {
    const roles = (userStore.currentUser?.roles || []) as Role[]
    const currentTeamId = teamStore.currentTeam?.id
    return roles.filter((role: any) => {
      const teamCtx = role.context?.teams
      if (!teamCtx || teamCtx.length === 0) return true
      return currentTeamId ? teamCtx.includes(currentTeamId) : false
    })
  }

  const hasPermission = (entity: string, action: ActionType): boolean => {
    if (!userStore.currentUser) return false

    // Check if any effective role has the permission for the entity and action
    return getEffectiveRoles().some((role: any) =>
      (role.permissions || []).some(
        (perm: any) => perm.entity === entity && perm.actions.some((a: any) => a.name === action)
      )
    )
  }

  const hasModule = (moduleName: string): boolean => {
    if (!userStore.currentUser) return false
    return getEffectiveRoles().some((role: any) =>
      (role.modules || []).some((mod: any) => mod.name === moduleName)
    )
  }

  const hasView = (viewSlug: string): boolean => {
    if (!userStore.currentUser) return false
    return getEffectiveRoles().some((role: any) =>
      (role.modules || []).some((mod: any) =>
        (mod.views || []).some((view: any) => view.slug === viewSlug),
      ),
    )
  }

  /**
   * Returns true when the current user has at least one role whose per-role
   * context covers `entityId` under `entityType`. Optionally restrict the
   * check to a specific role name (e.g. only count it when the matching role
   * is `team_leader`).
   */
  const hasContextRole = (
    entityType: string,
    entityId: string,
    roleName?: string,
  ): boolean => {
    const roles = userStore.currentUser?.roles
    if (!roles) return false
    return roles.some((role: any) => {
      if (roleName && role.name !== roleName) return false
      const ids = role.context?.[entityType]
      return Array.isArray(ids) && ids.includes(entityId)
    })
  }

  return {
    hasPermission,
    hasModule,
    hasView,
    hasContextRole
  }
}
